import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { DEVICE_SIZE, mq } from '../../utils/mq';

const Dropdown = styled.div`
  font-family: var(--theme-typography-primary-font);
  position: relative;
  z-index: 2;
`;

const Button = styled.div`
  align-items: stretch;
  border-radius: 6px;
  display: flex;
  height: 60px;
  overflow: hidden;
  width: 100%;
`;

const ButtonLinkA = styled.a`
  align-items: center;
  background: var(--theme-palette-green);
  border-bottom-left-radius: 8px;
  border-top-left-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  transition: background 0.3s;
  width: 100%;
  z-index: 1;
  text-decoration: none;

  &:hover {
    background-color: var(--theme-palette-dark-green);
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 24px;
      justify-content: center;
    `,
  )}
`;

const ButtonLinkDiv = styled.div`
  align-items: center;
  background: var(--theme-palette-green);
  border-bottom-left-radius: 8px;
  border-top-left-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  font-size: 18px;
  transition: background 0.3s;
  width: 100%;
  z-index: 1;

  &:hover {
    background-color: var(--theme-palette-dark-green);
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 24px;
      justify-content: center;
    `,
  )}
`;

const ButtonLinkLabel = styled.span`
  margin: 0 0 0 15px;
`;

const ButtonAccordion = styled.div`
  background: var(--theme-palette-green);
  border-bottom-right-radius: 8px;
  border-top-right-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0 22px 0 16px;
  position: relative;
  z-index: 1;

  &::before {
    border: solid 1px var(--theme-palette-white);
    content: '';
    height: 29px;
    left: 0;
    position: absolute;
  }

  &:hover {
    background-color: var(--theme-palette-dark-green);
  }
`;

const ButtonAccordionWrapper = styled.div`
  transform: initial;
  transition: all 0.3s;

  &.mod-top {
    position: relative;
    top: -2px;
  }

  &.mod-rotate {
    top: 0;
    transform: rotate(180deg);
  }
`;

const Accordion = styled.div`
  background-color: var(--theme-color-background);
  border: solid 2px #eeeeee;
  border-radius: 0px 0px 6px 6px;
  box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.1);
  display: none;
  flex-direction: column;
  left: 5px;
  position: absolute;
  right: 5px;
  top: 98%;

  &.mod-open {
    display: flex;

    strong {
      font-size: 18px;
    }
  }
`;
const AccordionItemA = styled.a`
  border-bottom: solid 1px #dddddd;
  cursor: pointer;
  margin: 0 15px;
  padding: 17px 0;
  user-select: none;
  text-decoration: none;

  &:last-of-type {
    border: none;
  }
`;

const AccordionItemDiv = styled.div`
  border-bottom: solid 1px #dddddd;
  cursor: pointer;
  margin: 0 15px;
  padding: 17px 0;
  user-select: none;

  span {
    align-items: center;
    flex-direction: row;
    font-size: 16px;
    gap: 6px;
    line-height: 21px;
  }

  &:last-of-type {
    border: none;
  }
`;

export type ButtonDropdownProps = {
  links: {
    title: any;
    onClick?: () => void;
    a?: {
      href?: string;
      rel?: string;
    };
  }[];
  isRotate: Boolean;
};

export const ButtonDropdown = ({ ...props }: React.PropsWithChildren<ButtonDropdownProps>) => {
  const { links, isRotate, children } = props;

  const [mainLink, setMainLink] = useState(links[0]);
  const [otherLinks, setOtherLinks] = useState(links.slice(1));
  const [isOpen, setIsOpen] = useState<Boolean>(false);

  useEffect(() => {
    setMainLink(links[0]);
    setOtherLinks(links.slice(1));
  }, [links]);

  const ButtonLink: React.FC<React.PropsWithChildren<{}>> = useMemo(
    () =>
      mainLink?.a
        ? ({ children: c }) => (
            <ButtonLinkA href={mainLink.a.href} rel={mainLink.a.rel}>
              {c}
            </ButtonLinkA>
          )
        : ({ children: c }) => <ButtonLinkDiv onClick={mainLink?.onClick}>{c}</ButtonLinkDiv>,
    [mainLink],
  );

  return (
    <Dropdown>
      <Button>
        <ButtonLink>
          <ButtonLinkLabel>{mainLink?.title}</ButtonLinkLabel>
        </ButtonLink>
        {otherLinks.length ? (
          <ButtonAccordion onClick={() => setIsOpen(!isOpen)}>
            <ButtonAccordionWrapper className={classNames({ 'mod-rotate': isRotate && isOpen, 'mod-top': true })}>
              {children}
            </ButtonAccordionWrapper>
          </ButtonAccordion>
        ) : (
          children && (
            <ButtonAccordion onClick={mainLink?.onClick}>
              <ButtonAccordionWrapper>{children}</ButtonAccordionWrapper>
            </ButtonAccordion>
          )
        )}
      </Button>

      <Accordion className={isOpen ? 'mod-open' : ''}>
        {otherLinks.map((link, i) => {
          if (link?.a) {
            return (
              <AccordionItemA key={i} href={link.a.href} rel={link.a.rel}>
                {link?.title}
              </AccordionItemA>
            );
          }

          return (
            <AccordionItemDiv key={i} onClick={link.onClick}>
              {link?.title}
            </AccordionItemDiv>
          );
        })}
      </Accordion>
    </Dropdown>
  );
};
